/**
 * Pegasus-native customer notes (NOTES-WRITE-01) — the first real system-of-
 * record WRITE on the customer ([[project_pegasus_system_of_record]]). ESC notes
 * stay read-only (DEC-22); notes the office types here live in Supabase. Saving
 * one closes the NOTES-AI-02 loop: it clears the extraction marker + re-runs
 * extract-notes (which now reads pegasus_note first), so a typed note can become
 * a "Good to know" pill within seconds.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface PegasusNote {
  id: string;
  custno: string;
  locno: string | null;
  body: string;
  author: string | null;
  pinned: boolean;
  created_at: string;
}

export function usePegasusNotes(custno: string | undefined) {
  return useQuery({
    queryKey: ["pegasus_notes", custno],
    enabled: !!custno,
    staleTime: 30_000,
    queryFn: async (): Promise<PegasusNote[]> => {
      const { data, error } = await supabase
        .from("pegasus_note")
        .select("*")
        .eq("custno", custno as string)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as PegasusNote[];
    },
  });
}

export function useAddPegasusNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string; locno?: string | null; body: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_note").insert({
        custno: v.custno,
        locno: v.locno ?? null,
        body: v.body.trim(),
        author: u?.user?.email ?? null,
      });
      if (error) throw error;
      // Close the loop: drop the marker + re-read this customer's notes so the
      // new note can surface as a pill. Awaited so the signals are ready when we
      // refresh — the Save button just shows "Saving…" for the extra second.
      await supabase.from("note_extraction_done").delete().eq("custno", v.custno);
      await supabase.functions.invoke("extract-notes", { body: { custno: v.custno } });
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["pegasus_notes", v.custno] });
      qc.invalidateQueries({ queryKey: ["customer_signals", v.custno] });
      qc.invalidateQueries({ queryKey: ["note_extraction_marker", v.custno] });
    },
  });
}

export function useDeletePegasusNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; custno: string }) => {
      const { error } = await supabase.from("pegasus_note").delete().eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["pegasus_notes", v.custno] }),
  });
}
